/**
 * React Application Entry Point
 *
 * This is the main entry point for the LexAI Dashboard React application.
 * It handles the initial mounting of the React application to the DOM.
 *
 * Key Responsibilities:
 * - Creates the React root container using React 18's createRoot API
 * - Renders the main App component wrapped in StrictMode for development
 * - Initializes performance monitoring with reportWebVitals
 * - Sets up global error handlers for unhandled promise rejections and errors
 *
 * Architecture Notes:
 * - Uses React 18's concurrent features via createRoot
 * - StrictMode enables additional development checks and warnings
 * - Performance metrics are collected but not sent by default
 * - CSS is imported first to ensure styles are available during component rendering
 * - Global error handlers catch errors not handled by React's error boundary
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css'; // Global styles - imported before components for proper styling
import App from './App';
import reportWebVitals from './reportWebVitals';

/**
 * Global Error Handlers
 *
 * These handlers catch errors that aren't handled by React's error boundary system,
 * including unhandled promise rejections and uncaught exceptions.
 */

/**
 * Handle unhandled promise rejections
 * Logs the error and prevents the application from crashing silently
 */
const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  console.error('Unhandled Promise Rejection:', {
    reason: event.reason,
    promise: event.promise,
    type: 'UNHANDLED_PROMISE_REJECTION'
  });

  // Prevent default browser handling (which might show an error overlay)
  event.preventDefault();
};

/**
 * Handle uncaught JavaScript errors
 * Logs the error for debugging purposes
 */
const handleUncaughtError = (event: ErrorEvent) => {
  // Ignore errors from browser extensions (they're not our code)
  if (event.filename?.includes('chrome-extension://') || 
      event.filename?.includes('moz-extension://') ||
      event.filename?.includes('safari-extension://')) {
    console.debug('Ignoring browser extension error:', event.message);
    event.preventDefault(); // Prevent error overlay for extension errors
    return;
  }

  console.error('Uncaught Error:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
    type: 'UNCAUGHT_ERROR'
  });

  // Allow default browser error handling to continue for real app errors
};

// Register global error handlers
window.addEventListener('unhandledrejection', handleUnhandledRejection);
window.addEventListener('error', handleUncaughtError);

/**
 * Create React Root
 *
 * React 18's createRoot enables concurrent features and improved performance.
 * It replaces the legacy ReactDOM.render method.
 */
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement // Mount point defined in public/index.html
);

/**
 * Render Application
 *
 * StrictMode enables additional development checks:
 * - Detects unsafe lifecycle methods
 * - Warns about deprecated features
 * - Helps identify side effects in components
 *
 * App component handles MSAL initialization and authentication flow.
 */
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

/**
 * Performance Monitoring
 *
 * reportWebVitals measures and reports web performance metrics:
 * - First Contentful Paint (FCP)
 * - Largest Contentful Paint (LCP)
 * - First Input Delay (FID)
 * - Cumulative Layout Shift (CLS)
 *
 * By default, metrics are logged to console. To send to analytics:
 * reportWebVitals(console.log) or reportWebVitals(sendToAnalytics)
 *
 * Learn more: https://bit.ly/CRA-vitals
 */
reportWebVitals();
